import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Switch,
    Typography,
    useMediaQuery,
} from '@mui/material';
import TodayIcon from '@mui/icons-material/Today';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { useInterval } from 'react-use';
import { getCitasCalendario, getEstados, getTareasCalendario, postMoverEventoCalendario } from '../../services/apis';
import { CalendarioGrid } from './CalendarioGrid';
import { CalendarioMovil } from './CalendarioMovil';
import { CalendarioFiltros, FILTRO_TODOS } from './CalendarioFiltros';
import { CrearCitaCalendarioDialog } from './CrearCitaCalendarioDialog';
import { TarjetaTarea } from './TarjetaTarea';
import {
    cambiarDiaEvento,
    etiquetaMesCalendario,
    formatearIdDia,
    normalizarCitasCalendario,
    normalizarTareasCalendario,
    parsearIdDia,
} from './calendarioUtils';
import { SubHeader } from '../../utils/componentes-utiles/SubHeader';

const filtrosIniciales = {
    search: '',
    proyecto: FILTRO_TODOS,
    estado: FILTRO_TODOS,
};

const normalizarTexto = (value) =>
    String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const hayDialogAbierto = () =>
    Boolean(document.querySelector('.MuiDialog-root [role="dialog"], .swal2-container'));

export const Calendario = () => {
    const isMobile = useMediaQuery('(max-width:900px)');
    const hoy = new Date();
    const [tareas, setTareas] = useState([]);
    const [estados, setEstados] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [mesActual, setMesActual] = useState(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
    const [fechaSeleccionada, setFechaSeleccionada] = useState(formatearIdDia(hoy));
    const [filtros, setFiltros] = useState(filtrosIniciales);
    const [openCrearCita, setOpenCrearCita] = useState(false);
    const [tareaArrastrando, setTareaArrastrando] = useState(null);
    const [mostrarFinesSemana, setMostrarFinesSemana] = useState(false);

    const cargarCalendario = async (mostrarCarga = true) => {
        if (mostrarCarga) {
            setLoaded(false);
        }

        try {
            const [responseTareas, responseCitas, responseEstados] = await Promise.all([
                getTareasCalendario(),
                getCitasCalendario(),
                getEstados(),
            ]);

            const tareasNormalizadas = normalizarTareasCalendario(responseTareas?.data || []);
            const citasNormalizadas = normalizarCitasCalendario(responseCitas?.data || []);
            const estadosResponse = responseEstados?.data?.estados || [];

            setTareas([...tareasNormalizadas, ...citasNormalizadas].sort((a, b) => a.inicio - b.inicio));
            setEstados(estadosResponse);
        } catch (error) {
            console.error('Error al cargar el calendario:', error);
            setTareas([]);
            setEstados([]);
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        cargarCalendario();
    }, []);

    useInterval(
        () => {
            if (hayDialogAbierto()) return;
            cargarCalendario(false);
        },
        tareaArrastrando || openCrearCita ? null : 30000
    );

    const opcionesProyecto = useMemo(() => {
        const mapa = new Map();

        tareas.forEach((tarea) => {
            const id = tarea.id_proyecto || tarea.proyecto;
            const label = tarea.proyecto || `Proyecto ${id}`;

            if (id && !mapa.has(String(id))) {
                mapa.set(String(id), { value: String(id), label });
            }
        });

        return Array.from(mapa.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [tareas]);

    const opcionesEstado = useMemo(() => {
        const desdeEstados = estados
            .filter((estado) => estado?.id)
            .map((estado) => ({
                value: String(estado.id),
                label: estado.nombre || estado.tipoEstado || `Estado ${estado.id}`,
            }));

        if (desdeEstados.length > 0) return desdeEstados;

        const mapa = new Map();
        tareas.forEach((tarea) => {
            if (tarea.id_estado && !mapa.has(String(tarea.id_estado))) {
                mapa.set(String(tarea.id_estado), {
                    value: String(tarea.id_estado),
                    label: tarea.estado || `Estado ${tarea.id_estado}`,
                });
            }
        });

        return Array.from(mapa.values());
    }, [estados, tareas]);

    const tareasFiltradas = useMemo(() => {
        const search = normalizarTexto(filtros.search);

        return tareas.filter((tarea) => {
            const coincideProyecto =
                filtros.proyecto === FILTRO_TODOS ||
                String(tarea.id_proyecto || tarea.proyecto) === String(filtros.proyecto);
            const coincideEstado =
                filtros.estado === FILTRO_TODOS ||
                String(tarea.id_estado) === String(filtros.estado);
            const textoTarea = normalizarTexto([
                tarea.id,
                tarea.titulo,
                tarea.descripcion,
                tarea.proyecto,
                tarea.cliente,
                tarea.correo,
                tarea.estado,
            ].join(' '));

            return coincideProyecto && coincideEstado && (!search || textoTarea.includes(search));
        });
    }, [tareas, filtros]);

    const handleDragStart = (event) => {
        const tareaActiva = tareas.find((tarea) => String(tarea.id) === String(event.active?.id));
        setTareaArrastrando(tareaActiva || null);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        const tareaActiva = tareas.find((tarea) => String(tarea.id) === String(active?.id));

        setTareaArrastrando(null);

        if (!over || !active?.id || !tareaActiva) return;

        setTareas((prev) =>
            prev.map((tarea) =>
                String(tarea.id) === String(active.id)
                    ? cambiarDiaEvento(tarea, over.id)
                    : tarea
            )
        );
        setFechaSeleccionada(over.id);

        const response = await postMoverEventoCalendario({
            tipo: tareaActiva.tipo_calendario === 'cita' ? 'cita' : 'tarea',
            id: tareaActiva.tipo_calendario === 'cita' ? tareaActiva.id_cita : tareaActiva.id,
            fecha: over.id,
        });

        if (!response?.success) {
            console.error('No se pudo persistir el movimiento del calendario:', response);
            cargarCalendario();
        }
    };

    const handleDragCancel = () => {
        setTareaArrastrando(null);
    };

    const handleSelectDay = (idDia) => {
        setFechaSeleccionada(idDia);
        const [, month, year] = String(idDia).split('-').map(Number);
        setMesActual(new Date(year, month - 1, 1));
        setOpenCrearCita(true);
    };

    const handleFocusDay = (idDia) => {
        setFechaSeleccionada(idDia);
        const [, month, year] = String(idDia).split('-').map(Number);
        setMesActual(new Date(year, month - 1, 1));
    };

    const cambiarMes = (delta) => {
        const [day, month, year] = fechaSeleccionada.split('-').map(Number);
        const mesDestino = new Date(year, month - 1 + delta, 1);
        const ultimoDiaMesDestino = new Date(
            mesDestino.getFullYear(),
            mesDestino.getMonth() + 1,
            0
        ).getDate();
        const diaDestino = Math.min(day, ultimoDiaMesDestino);
        const fechaDestino = new Date(mesDestino.getFullYear(), mesDestino.getMonth(), diaDestino);

        setFechaSeleccionada(formatearIdDia(fechaDestino));
        setMesActual(new Date(fechaDestino.getFullYear(), fechaDestino.getMonth(), 1));
    };

    const cambiarSemana = (delta) => {
        const [day, month, year] = fechaSeleccionada.split('-').map(Number);
        const next = new Date(year, month - 1, day + delta);
        setFechaSeleccionada(formatearIdDia(next));
        setMesActual(new Date(next.getFullYear(), next.getMonth(), 1));
    };

    const irAHoy = () => {
        const today = new Date();
        setFechaSeleccionada(formatearIdDia(today));
        setMesActual(new Date(today.getFullYear(), today.getMonth(), 1));
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                width: { xs: 'calc(100% - 8px)', md: 'calc(100% - 6px)' },
                maxWidth: 2240,
                mx: 'auto',
                py: { xs: 1.3, md: 1.8 },
            }}
        >
            <Stack spacing={2}>
                <SubHeader />

                <Card
                    sx={{
                        borderRadius: '20px',
                        border: '1px solid rgba(229, 57, 53, 0.12)',
                        boxShadow: '0 10px 24px rgba(68, 47, 44, 0.08)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            justifyContent="space-between"
                        >
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                    Calendario
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                                    Planificacion de tareas por fecha y hora
                                </Typography>
                            </Box>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                justifyContent="flex-end"
                                spacing={1}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={mostrarFinesSemana}
                                            onChange={(event) => setMostrarFinesSemana(event.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Fines de semana"
                                    sx={{
                                        m: 0,
                                        mr: { sm: 1 },
                                        '.MuiFormControlLabel-label': {
                                            fontSize: 13,
                                            fontWeight: 700,
                                        },
                                    }}
                                />

                                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                                    <IconButton onClick={() => cambiarMes(-1)}>
                                        <ArrowBackIosNewIcon fontSize="small" />
                                    </IconButton>
                                    <Typography
                                        sx={{
                                            minWidth: { xs: 150, sm: 190 },
                                            textAlign: 'center',
                                            fontWeight: 800,
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        {etiquetaMesCalendario(mesActual)}
                                    </Typography>
                                    <IconButton onClick={() => cambiarMes(1)}>
                                        <ArrowForwardIosIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton onClick={irAHoy} color="primary">
                                        <TodayIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                {/* <CalendarioFiltros
                    filtros={filtros}
                    opcionesProyecto={opcionesProyecto}
                    opcionesEstado={opcionesEstado}
                    onChange={(name, value) => setFiltros((prev) => ({ ...prev, [name]: value }))}
                    onReset={() => setFiltros(filtrosIniciales)}
                /> */}

                {!loaded ? (
                    <Box sx={{ minHeight: 300, display: 'grid', placeItems: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : isMobile ? (
                    <CalendarioMovil
                        referenceDate={parsearIdDia(fechaSeleccionada)}
                        fechaSeleccionada={fechaSeleccionada}
                        onExpandDay={handleFocusDay}
                        onCreateDay={handleSelectDay}
                        onPrevWeek={() => cambiarSemana(-7)}
                        onNextWeek={() => cambiarSemana(7)}
                        onToday={irAHoy}
                        tareas={tareasFiltradas}
                        onChanged={() => cargarCalendario(false)}
                        mostrarFinesSemana={mostrarFinesSemana}
                    />
                ) : (
                    <DndContext
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                    >
                        <Grid container>
                            <Grid item xs={12}>
                                <CalendarioGrid
                                    mesActual={mesActual}
                                    tareas={tareasFiltradas}
                                    onSelectDay={handleSelectDay}
                                    onChanged={() => cargarCalendario(false)}
                                    mostrarFinesSemana={mostrarFinesSemana}
                                />
                            </Grid>
                        </Grid>
                        <DragOverlay zIndex={99999}>
                            {tareaArrastrando ? (
                                <Box sx={{ width: 260, maxWidth: '80vw' }}>
                                    <TarjetaTarea
                                        id={tareaArrastrando.id}
                                        tarea={tareaArrastrando}
                                        overlay
                                    />
                                </Box>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                )}

                {loaded && (
                    <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>
                        {tareasFiltradas.length} {tareasFiltradas.length === 1 ? 'tarea visible' : 'tareas visibles'}
                    </Typography>
                )}

                <CrearCitaCalendarioDialog
                    open={openCrearCita}
                    fecha={fechaSeleccionada}
                    onClose={() => setOpenCrearCita(false)}
                    onCreated={cargarCalendario}
                />
            </Stack>
        </Container>
    );
};
